import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWorkspaceId } from '../../contexts/WorkspaceContext';
import { queryKeys, STALE_TIME } from '../../lib/queryClient';
import { fetchFolderPage, folderListParams } from './useFolderItems';
import type { WorkspaceView } from '../../stores/workspaceViewPrefsStore';
import { useBarePathViewParams } from './useWorkspaceViewParams';

/**
 * Returns a hover/focus handler that warms a folder's first page.
 *
 * Opening a folder navigates to its bare path, so the destination reads the
 * remembered workspace-wide sort and filters with the default scope
 * (`useWorkspaceViewParams`); that is the key warmed here, since a prefetch
 * under any other key would never be read. Fresh entries are not refetched,
 * so repeated hovers cost nothing.
 *
 * `shownView` maps the remembered layout to the one the destination draws,
 * for a surface that draws another (the phone shows Cards as its list), since
 * the layout decides whether the list mixes folders in.
 */
export function usePrefetchFolder(shownView?: (remembered: WorkspaceView) => WorkspaceView) {
  const workspaceId = useWorkspaceId();
  const queryClient = useQueryClient();
  const remembered = useBarePathViewParams();
  const viewParams = useMemo(
    () => (shownView ? { ...remembered, view: shownView(remembered.view) } : remembered),
    [remembered, shownView],
  );

  return useCallback(
    (folderId: string | null): Promise<void> => {
      if (!workspaceId) return Promise.resolve();
      const params = folderListParams(folderId, viewParams);
      return queryClient.prefetchInfiniteQuery({
        queryKey: queryKeys.workspaceItems.list(workspaceId, params),
        queryFn: ({ signal }) =>
          fetchFolderPage(queryClient, workspaceId, params, undefined, signal),
        initialPageParam: undefined as string | undefined,
        staleTime: STALE_TIME.DEFAULT,
      });
    },
    [queryClient, workspaceId, viewParams],
  );
}
